import React, { useCallback, useState } from "react";
import { styled } from "goober";

import { classNames } from "@/util/class-names.mjs";
import useCallbackRef from "@/util/use-callback-ref.mjs";

import type { TextFieldValidationProps } from "../util/use-validation-error.mjs";
import useValidationError from "../util/use-validation-error.mjs";

export const InputContainer = styled("div")`
  --bg-color: var(--shade7);

  position: relative;

  display: grid;
  grid-template:
    "input"
    "error" / minmax(0, 1fr);

  font-weight: 400;
  font-size: var(--sp-3_5);

  & > * {
    grid-area: input;
  }

  input {
    height: var(--sp-12);
  }

  & > .sizer,
  textarea {
    margin: 0;
  }

  & > .sizer,
  input,
  textarea {
    grid-area: input;

    padding: var(--sp-3);

    color: var(--shade0);

    background-color: var(--bg-color);
    border: 1px solid var(--shade6);
    border-radius: var(--br);

    resize: none;

    transition: border-color 0.15s;

    &::placeholder {
      color: var(--shade2);
      opacity: 75%;
    }

    &:hover,
    &:focus {
      border-color: var(--shade3);

      outline: none;
    }

    &:disabled {
      --bg-color: var(--shade8);

      color: var(--shade2);

      cursor: not-allowed;
    }

    & ~ div[role="alert"] {
      grid-area: error;

      color: var(--primary);
      font-weight: 400;
      font-size: var(--sp-3);

      opacity: 0;

      transition:
        opacity 0.25s,
        transform 0.25s;

      &:not(.inline) {
        position: absolute;
        bottom: 0;

        transform: translateY(50%);
      }

      &.inline {
        height: 0;

        transform: translateY(-50%);
      }
    }
  }

  & > .sizer {
    white-space: pre-wrap;
    word-wrap: break-word;
    visibility: hidden;
  }

  &[data-validate="true"] {
    input:invalid,
    textarea:invalid {
      background-color: hsl(var(--primary-hsl) / 15%);
      background-color: color-mix(in srgb, var(--bg-color), var(--primary) 15%);

      border-color: var(--primary);

      &:hover,
      &:focus {
        border-color: var(--primary-hover);
      }

      & ~ div[role="alert"] {
        opacity: 1;

        &:not(.inline) {
          transform: translateY(100%);
        }

        &.inline {
          flex-basis: 100%;

          height: unset;

          transform: translateY(0%);
        }
      }
    }
  }

  &.invert {
    --bg-color: var(--shade0);

    color: var(--shade10);

    input::placeholder,
    textarea::placeholder {
      color: var(--shade3);
    }

    input:disabled,
    textarea::disabled {
      --bg-color: var(--shade1);

      color: var(--shade5);
    }

    &[data-validate="true"] {
      input:invalid,
      textarea:invalid {
        background-color: hsl(350deg 56% 71%);
        background-color: color-mix(
          in srgb,
          var(--bg-color),
          var(--primary) 50%
        );
      }
    }
  }
`;

type InputFieldProps = {
  invert?: boolean;
} & React.ComponentProps<"input">;

type TextFieldProps = TextFieldValidationProps & InputFieldProps;

export const TextField = ({
  invert,

  className,

  error,
  errorInline,

  children,

  ...rest
}: TextFieldProps) => {
  const [shouldShowError, setShouldShowError] = useState(false);

  const onBlur = useCallback(() => {
    // If the field is required, show the error immediately otherwise only show if
    // the field has a value
    setShouldShowError(rest.required || !!rest.value);
  }, [setShouldShowError, rest.required, rest.value]);

  const inputRef = useCallbackRef((element: HTMLInputElement) => {
    if (!element) return;

    function preventPopup(e: Event) {
      setShouldShowError(true);

      e.preventDefault();
    }

    element.addEventListener("invalid", preventPopup, true);

    return () => {
      element.removeEventListener("invalid", preventPopup, true);
    };
  });

  const errorMessage = useValidationError(inputRef, { error, ...rest });

  return (
    <InputContainer
      {...classNames(className, invert && "invert")}
      data-validate={(shouldShowError || !!error) === true ? true : undefined}
    >
      <input
        ref={inputRef}
        onBlur={onBlur}
        {...(shouldShowError && errorMessage ? { "aria-invalid": "true" } : {})}
        {...rest}
        // Fixes some password managers inserting props on page load, leading to hydration errors
        suppressHydrationWarning
      />

      {children}

      <div role="alert" {...classNames(errorInline && "inline")}>
        {errorMessage}
      </div>
    </InputContainer>
  );
};
